import React from 'react';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  styled,
  Typography,
} from '@mui/material';
// Components
import { FormikCheckBox, FormikField } from '../formik';
import { Message } from '@components';
// Utils
import { Api, getToken, setToken } from '@utils';
import { useAuthContext } from './AuthContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// ----------------------------------------------------------------------

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Invalid email address')
    .matches(
      /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
      'Invalid email address'
    )
    .required('Email is required')
    .trim(),
  password: yup.string().required('Password is required').trim(),
});

const initialValues = {
  email: '',
  password: '',
  checkbox: true,
};

const SignUp = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginTop: theme.spacing(3),

  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    opacity: 0.8,
    transition: 'all 0.3s ease-in-out',

    '&:hover': {
      opacity: 1,
      color: theme.palette.text.primary,
    },
  },
}));

const LoginForm = () => {
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const { setIsAuthenticated } = useAuthContext();

  // Handle form submit
  const handleSubmitForm = async (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues> | any
  ) => {
    // Reset cache
    localStorage.removeItem('steps');
    localStorage.removeItem('token');
    localStorage.removeItem('sidToken');

    try {
      const { data } = await Api.post('/login', values);
      if (data.success) {
        // Save token to local storage
        setIsAuthenticated(true);
        setToken(data.data.token);

        // Save the redirect
        await saveRedirect(data.data.token);

        setSuccess(true);
        setError('');
      }
    } catch (error: any) {
      setError(error);
      setToken('');
      setSuccess(false);
    }

    helpers.setTouched({});
  };

  const saveRedirect = async (token: string) => {
    // get query string from url
    const queryString = window.location.search;

    // Check if query string contains hmac, code, host, shop
    if (
      queryString.includes('hmac') &&
      queryString.includes('code') &&
      queryString.includes('host') &&
      queryString.includes('shop')
    ) {
      // save to db
      const { data } = await Api.put(
        '/profile',
        { redirect: queryString },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        // Save to localStorage
        localStorage.setItem('redirect', queryString);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormikField
            name="email"
            label="Email"
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
          />

          <FormikField
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ my: 3 }}>
            <FormikCheckBox
              name="checkbox"
              label={
                <Typography
                  variant="body2"
                  pt={0.15}
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#1a1f36',
                  }}
                >
                  Stay signed in for a week
                </Typography>
              }
            />
          </Box>

          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            color="primary"
            fullWidth
          >
            Continue
          </Button>

          {error && <Message sx={{ mt: 3 }} type="error" title={error} />}
          {!error && success && (
            <Message
              sx={{ mt: 3 }}
              type="success"
              title="Logged in successfully! Redirecting..."
            />
          )}

          <SignUp variant="body2">
            Don't have an account? <Link to="/register">Sign up</Link>
          </SignUp>
        </Form>
      )}
    </Formik>
  );
};

export default LoginForm;
